import Vue from "vue";
import ResourceService from "./resource.service";

class FinancingService extends ResourceService {
  static get resource() {
    return "financing";
  }

  /**
   * Get financing data for a specific business
   * @param {string|number} businessId - The business ID
   * @returns {Promise} - The financing data or null if not found
   */
  static getFinancingForBusiness(businessId) {
    const url = `${this.resource}/${businessId}`;

    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          // If financing data doesn't exist, return null instead of throwing
          if (error.response && error.response.status === 404) {
            resolve(null);
          } else {
            reject(error);
          }
        });
    });
  }

  /**
   * Create new financing record
   * @param {Object} financingData - The financing data to create
   * @returns {Promise} - The created financing record
   */
  static createFinancing(financingData) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(this.resource + "/", financingData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Update existing financing record
   * @param {string|number} creditLineId - The credit line ID to update
   * @param {Object} financingData - The financing data to update
   * @returns {Promise} - The updated financing record
   */
  static updateFinancing(creditLineId, financingData) {
    const url = `${this.resource}/${creditLineId}`;

    return new Promise((resolve, reject) => {
      Vue.axios
        .patch(url, financingData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get financing aggregate data
   * @param {string|number} businessId - The business ID for filtering
   * @returns {Promise} - The aggregate financing data
   */
  static getFinancingAggregate(businessId) {
    const url = `${this.resource}/aggregate?business_id=${businessId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get repayments data
   * @param {string} businessId - The business ID for filtering
   * @param {Object} queryParams - Optional query parameters (page, page_size, search, etc.)
   * @returns {Promise} - The repayments data
   */
  static getRepayments(businessId, queryParams = {}) {
    const url = `${this.resource}/repayments/`;
    const params = {
      business_id: businessId,
      ...queryParams
    };
    
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get monthly disbursed analytics
   * @returns {Promise} - The monthly disbursed analytics data
   */
  static getMonthlyDisbursedAnalytics(businessId) {
    const url = `${this.resource}/finance-request-analytics?business_id=${businessId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get finance request details by ID
   * @param {string} requestId - The finance request ID
   * @returns {Promise} - The finance request details
   */
  static getFinanceRequest(requestId) {
    const url = `${this.resource}/request/${requestId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Update finance request details
   * @param {string} requestId - The finance request ID
   * @param {Object} updateData - The data to update
   * @returns {Promise} - The updated finance request
   */
  static updateFinanceRequest(requestId, updateData) {
    const url = `${this.resource}/request/${requestId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .patch(url, updateData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get admin aggregate financing data
   * @returns {Promise} - The admin aggregate financing data
   */
  static getAdminAggregate(params = {}) {
    const url = `${this.resource}/admin/aggregate`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get admin requests with pagination and filters
   * @param {Object} params - Query parameters
   * @returns {Promise} - The admin requests data
   */
  static getAdminRequests(params = {}) {
    const url = `${this.resource}/admin/requests`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get eligible customers for a business
   * @param {string} businessId - The business ID
   * @param {Object} params - Query parameters
   * @returns {Promise} - The eligible customers data
   */
  static getEligibleCustomers(businessId, params = {}) {
    const url = `${this.resource}/eligible-business/`;
    const queryParams = { business_id: businessId, ...params };
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params: queryParams })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get admin repayments with pagination and filters
   * @param {Object} params - Query parameters
   * @returns {Promise} - The admin repayments data
   */
  static getAdminRepayments(params = {}) {
    const url = `${this.resource}/admin/repayments`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get finance requests for a business
   * @param {string} businessId - The business ID
   * @param {Object} params - Query parameters
   * @returns {Promise} - The finance requests data
   */
  static getFinanceRequests(businessId, params = {}) {
    const url = `${this.resource}/requests`;
    const queryParams = { business_id: businessId, ...params };
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params: queryParams })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get admin invoice requests with pagination and filters
   * @param {Object} params - Query parameters (business_id, finance_request_id, sort_by, page, page_size)
   * @returns {Promise} - The invoice requests data
   */
  static getInvoiceRequests(params = {}) {
    const url = `${this.resource}/admin/invoice-requests`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Create a new repayment
   * @param {Object} repaymentData - The repayment data to create
   * @returns {Promise} - The created repayment record
   */
  static createRepayment(repaymentData) {
    const url = `${this.resource}/repayments/`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(url, repaymentData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  static initiateZandPayment(requestId) {
    const url = `${this.resource}/zand/initiate/${requestId}/`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  static getZandPaymentStatus(requestId) {
    const url = `${this.resource}/zand/status/${requestId}/`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Get disbursement invoice PDF for a finance request
   * @param {string|number} financeRequestId - The finance request ID
   * @returns {Promise} - The PDF blob data
   */
  static getDisbursementInvoice(financeRequestId) {
    const url = `${this.resource}/disbursement-invoice/${financeRequestId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { responseType: 'blob' })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Normalize wrapped API payloads (e.g. { data: { ... } } or nested).
   * @param {*} payload - Raw axios response body
   * @returns {*} - Unwrapped entity or null
   */
  static unwrapEntity(payload) {
    if (payload == null) return null;
    let cur = payload;
    if (cur && typeof cur === "object" && "data" in cur) {
      cur = cur.data;
    }
    if (
      cur &&
      typeof cur === "object" &&
      !Array.isArray(cur) &&
      cur.data !== undefined &&
      typeof cur.data === "object" &&
      !Array.isArray(cur.data)
    ) {
      cur = cur.data;
    }
    return cur;
  }

  /**
   * @param {*} payload - List response body
   * @returns {Array} - Array rows
   */
  static unwrapListPayload(payload) {
    if (payload == null) return [];
    let d = payload.data !== undefined ? payload.data : payload;
    if (Array.isArray(d)) return d;
    if (d && typeof d === "object") {
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.results)) return d.results;
      if (Array.isArray(d.email_config)) return d.email_config;
    }
    return [];
  }

  /**
   * Get customer groups for financing
   * @param {Object} params - Query parameters (business_id, page, page_size, search)
   * @returns {Promise} - The customer groups data
   */
  static getCustomerGroups(params = {}) {
    const url = "customers/groups";
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Customers available to add to a group (picker).
   */
  static getAvailableCustomersForGroups(businessId, params = {}) {
    const url = "customers/groups/available-customers";
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, {
          params: { business_id: businessId, ...params },
        })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Single customer group (edit / detail).
   */
  static getCustomerGroup(groupId, businessId) {
    const url = `customers/groups/${groupId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params: { business_id: businessId } })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Reminder workflow options for the business (create flow).
   */
  static getEmailConfigList(businessId) {
    const url = "business/email-config-list";
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, { params: { business_id: businessId } })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Group-specific reminder workflow assignment (edit flow options + selection).
   */
  static getEmailConfigAssignment(businessId, groupId) {
    const url = "business/email-config-assignment";
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(url, {
          params: { business_id: businessId, group_id: groupId },
        })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Persist reminder workflow assignment after group save.
   */
  static postEmailConfigAssignment(businessId, body) {
    const url = "business/email-config-assignment";
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(url, body, { params: { business_id: businessId } })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Create a new customer group (business_id on query string).
   * @param {string} businessId - Business uuid
   * @param {Object} groupData - JSON body (no business_id required)
   * @returns {Promise} - The created customer group
   */
  static createCustomerGroup(businessId, groupData) {
    const url = "customers/groups";
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(url, groupData, { params: { business_id: businessId } })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Update a customer group (business_id on query string; do not send workflow_id in body).
   * @param {string} groupId - The group ID to update
   * @param {string} businessId - Business uuid
   * @param {Object} groupData - The group data to update
   * @returns {Promise} - The updated customer group
   */
  static updateCustomerGroup(groupId, businessId, groupData) {
    const url = `customers/groups/${groupId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .patch(url, groupData, { params: { business_id: businessId } })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Delete a customer group
   * @param {string} groupId - The group ID to delete
   * @param {string} businessId - Business uuid
   * @returns {Promise} - The deletion response
   */
  static deleteCustomerGroup(groupId, businessId) {
    const url = `customers/groups/${groupId}`;
    return new Promise((resolve, reject) => {
      Vue.axios
        .delete(url, { params: { business_id: businessId } })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

export default FinancingService; 